import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation } from 'react-query'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getTermList } from 'app/redux/store/features/terms'
import TermGridFilter from './components/TermGridFilter'
import TermGridData from './components/TermGridData'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'

/** Maps DevExtreme grid column names to DbTerm property names for /api/course/Term/list */
const sortColumnToServerProperty = (columnName) => {
    if (!columnName) return 'TermYear'
    const map = {
        termYear: 'TermYear',
        termName: 'TermName',
        termNumber: 'TermNumber',
        isCurrent: 'IsCurrent',
        dateStart: 'DateStart',
        dateEnd: 'DateEnd',
        createTime: 'CreateTime',
        updateTime: 'UpdateTime',
        createdBy: 'CreatedBy',
    }
    return map[columnName] ?? Capitalize(columnName)
}

const TermsGrid = () => {
    const dispatch = useDispatch()
    const [filter, setFilter] = useState({
        termYear: null,
        termNumber: null,
        termName: '',
        sort: 'TermYear',
        isDescending: false,
    })
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function userReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [termGrid, setTermGrid] = React.useReducer(
        userReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            const merged = { ...currentfilter, skip: 0, take: pageSize }
            dispatch(getTermList(merged, true))
            setFilter(merged)
            setCurrentPage(0)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getTermList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const sortProperty = sortColumnToServerProperty(sortOrder?.[0]?.columnName)
        // TermYear + server: isDescending false = newest first; grid "desc" on Term = newest first → send false.
        const isDescending =
            sortProperty === 'TermYear'
                ? sortOrder[0].direction !== 'desc'
                : sortOrder[0].direction !== 'asc'
        const currentFilter = {
            ...filter,
            sort: sortProperty,
            isDescending,
            skip: 0,
            take: pageSize,
        }
        dispatch(getTermList(currentFilter))
        setFilter(currentFilter)
        setCurrentPage(0)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        const refreshFilter = { ...filter, skip: 0, take: pageSize }
        dispatch(getTermList(refreshFilter, true))
        setFilter(refreshFilter)
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setTermGrid({ type: 'set-grid-filter', payload: value })
        },
        [setTermGrid]
    )

    const setFilterIconColor = React.useCallback(
        (value) => {
            setTermGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setTermGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        onClick={onRefresh}
                        fontSize={'large'}
                        sx={{
                            mb: -1.3,
                            mr: 0.5,
                            ml: -2,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Term List
                </Typography>
            }
            headerSx={{
                height: 40,
                bgcolor: 'success.main',
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -1.7, mr: -1 }}>
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!termGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <TermGridFilter
                    mutation={applyFilter}
                    display={termGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid sx={{ mb: 5 }}>
                <TermGridData
                    onRefresh={onRefresh}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    apiCallInProgress={apiCallInProgress}
                    setApiCallInProgress={setApiCallInProgress}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default TermsGrid
